import React, { useEffect, useState } from 'react';
import {
  VStack,
  HStack,
  View,
  Text,
  Pressable,
  Heading,
  Box,
  PresenceTransition,
} from 'native-base';
import { theme } from '@breakroom-nx/assets';

// Classes and Types
import { Room } from '@breakroom-nx/classes';

import { Vibe, UserGroup, ChatBubble } from '../../';
import { MapChatMessageBubble } from '../map-chat-message-bubble/map-chat-message-bubble';
import { BlurView } from 'expo-blur';
import { UserIcon as UsersIcon } from 'react-native-heroicons/solid';

import {
  useAppSelector,
  useAppDispatch,
  selectRoom,
  toggleChatRoomPanel,
  getChatRoomPanelState,
  useGetMembersForRoomQuery,
  useGetLastMessageForRoomQuery,
} from '@breakroom-nx/redux';

export interface MapChatProps {
  room: Room;
}

export function MapChat({ room }: MapChatProps) {
  const dispatch = useAppDispatch();
  const [showMore, setShowMore] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const { data: members, isLoading: isLoadingMembers } =
    useGetMembersForRoomQuery(room);
  const { data: lastMessage } = useGetLastMessageForRoomQuery(room.id);
  const chatRoomPaneDisplayed = useAppSelector(getChatRoomPanelState);

  const renderVibe = () => {
    return <Vibe text={room.vibe} icon={room.vibeIcon} key={room.vibe} />;
  };

  const showAdditionalInfo = () => {
    setShowMore(true);
  };

  const hideAdditionalInfo = () => {
    setShowMore(false);
  };

  useEffect(() => {
    if (lastMessage) {
      setShowMessage(true);
      const timeout = setTimeout(() => setShowMessage(false), 5000);
      return () => {
        clearTimeout(timeout);
      };
    } else {
      return;
    }
  }, [lastMessage]);

  return (
    <VStack space={3} alignItems={'flex-start'}>
      {lastMessage &&
      lastMessage.length > 0 &&
      lastMessage[0].text !== undefined ? (
        <View position={'absolute'} left={'0px'} bottom={'70px'}>
          <PresenceTransition
            visible={showMessage}
            initial={{
              opacity: 0,
              scaleX: 0,
              scaleY: 0,
              translateX: -30,
              translateY: 30,
            }}
            animate={{
              opacity: 1,
              scaleX: 1,
              scaleY: 1,
              translateX: 5,
              translateY: -5,
              transition: {
                duration: 500,
              },
            }}
          >
            <MapChatMessageBubble
              lastMessage={lastMessage[0].text}
              backgroundColor={room.theme}
            />
          </PresenceTransition>
        </View>
      ) : null}

      <Pressable
        onHoverIn={showAdditionalInfo}
        onHoverOut={hideAdditionalInfo}
        onPress={() => {
          // console.log(`DISPATCHING ROOM: ${room.title}`);
          dispatch(selectRoom(room));
          if (!chatRoomPaneDisplayed) {
            dispatch(toggleChatRoomPanel());
          }
          // showAdditionalInfo();
        }}
      >
        <View
          rounded={'full'}
          overflow={'hidden'}
          borderColor={'surface.400'}
          borderWidth={showMore ? 2 : 0}
        >
          {showMore ? (
            <HStack
              space={3}
              textAlign={'left'}
              style={{
                padding: showMore ? 10 : 0,
                backgroundColor: `rgba(0, 0, 0, ${showMore ? 0.5 : 0})`,
              }}
            >
              <ChatBubble room={room} key={room.id} disabled={true} />
              {showMore ? (
                <VStack space={1} paddingRight={3}>
                  <HStack
                    justifyContent={'space-between'}
                    alignItems={'center'}
                  >
                    <Heading fontSize={19}>
                      <Text color={theme.colors.yellow[200]}>{room.title}</Text>
                    </Heading>
                  </HStack>

                  <HStack alignItems={'flex-start'} space={3}>
                    {renderVibe()}
                    {members && (
                      <HStack space={5}>
                        <HStack
                          space={1}
                          alignItems={'center'}
                          justifyContent={'center'}
                        >
                          <UsersIcon
                            size={14}
                            color={theme.colors.surface[600]}
                          />
                          <Text
                            fontSize={15}
                            fontWeight={600}
                            color={theme.colors.surface[600]}
                          >
                            {members?.length.toLocaleString()}
                          </Text>
                        </HStack>
                        <Box>
                          <UserGroup
                            compressed={true}
                            borderColor={'white'}
                            members={members}
                            loading={isLoadingMembers}
                            size={7}
                          />
                        </Box>
                      </HStack>
                    )}
                  </HStack>
                </VStack>
              ) : null}
            </HStack>
          ) : (
            <ChatBubble room={room} key={room.id} disabled={true} />
          )}
        </View>
      </Pressable>
    </VStack>
  );
}

export default React.memo(MapChat);
